import Text from "@src/lib/ui/Text";
import gql from "graphql-tag";
import React, { useMemo, useCallback } from "react";
import { Switch } from "@src/lib/ui/Switch";
import { GraphqlOperations } from "@src/types.generated";
import useUser from "@src/features/app/hooks/useUser";
import { Label } from "@src/lib/ui/Label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@src/lib/ui/Accordion";
import {
  useOrgFeatureFlagsQuery,
  useSetOrgFeatureFlags_FeatureFlagsQuery,
  useUpdateOrgFeatureFlagsMutation,
} from "./SetOrgFeatureFlags.generated";

const SetOrgFeatureFlags: React.FC = () => {
  const { isStaff } = useUser();
  const { data: orgFeatureFlagsData } = useOrgFeatureFlagsQuery();

  const allFlags = useSetOrgFeatureFlags_FeatureFlagsQuery();
  const updatableFlags = useMemo(() => {
    if (!allFlags.data?.FeatureFlags) return [];
    return allFlags.data.FeatureFlags.filter((f) => f.staffSelfServableForOrg);
  }, [allFlags.data?.FeatureFlags]);

  const [updateFlagsMutation] = useUpdateOrgFeatureFlagsMutation({
    refetchQueries: [GraphqlOperations.Query.OrgFeatureFlags],
  });

  const updateFlags = useCallback(
    (name: string, enabled: boolean) => {
      updateFlagsMutation({
        variables: {
          input: {
            featureFlags: [
              {
                name,
                enabled: { value: enabled },
              },
            ],
          },
        },
        refetchQueries: [
          GraphqlOperations.Query.OrgFeatureFlags,
          GraphqlOperations.Query.FeatureFlags,
        ],
      });
    },
    [updateFlagsMutation],
  );

  const orgFlags: Record<string, boolean> = useMemo(() => {
    if (!orgFeatureFlagsData?.OrgFeatureFlags)
      return {} as Record<string, boolean>;
    return orgFeatureFlagsData.OrgFeatureFlags.reduce(
      (acc, f) => {
        acc[f.name] = f.enabled;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [orgFeatureFlagsData?.OrgFeatureFlags]);

  const featureFlagToggleRows = useMemo(() => {
    return updatableFlags.map((f) => (
      <FeatureFlagToggleRow
        key={f.name}
        title={f.userFacingName || f.name}
        selected={orgFlags[f.name]}
        onUpdate={(checked) => updateFlags(f.name, checked)}
      />
    ));
  }, [orgFlags, updateFlags, updatableFlags]);

  if (!isStaff) return null;

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="feature-flags">
        <AccordionTrigger>
          <Text variant="body-title">Org level feature flags</Text>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-1.5">{featureFlagToggleRows}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

type FeatureFlagToggleRowProps = {
  title: string;
  selected: boolean;
  onUpdate: (checked: boolean) => void;
};

const FeatureFlagToggleRow: React.FC<FeatureFlagToggleRowProps> = (props) => (
  <div className="flex items-center py-0.5">
    <Label htmlFor={props.title} className="flex-1">
      {props.title}
    </Label>
    <Switch
      id={props.title}
      checked={props.selected}
      onCheckedChange={props.onUpdate}
    />
  </div>
);

gql`
  mutation UpdateOrgFeatureFlags($input: UpdateOrgFeatureFlagsInput!) {
    updateOrgFeatureFlags(input: $input) {
      success
    }
  }

  query OrgFeatureFlags {
    OrgFeatureFlags {
      name
      enabled
    }
  }

  query SetOrgFeatureFlags_FeatureFlags {
    FeatureFlags {
      name
      enabled
      userFacingName
      staffSelfServableForOrg
    }
  }
`;

export default SetOrgFeatureFlags;
